import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/http";

/**
 * Cosmetic loadout — active theme, equipped exclusive badges, active
 * animation flags. Reads from `/api/client/gamification/loadout` on mount
 * and pushes updates back to the backend. Provides `themeGradient` helper
 * so hero surfaces can honour the user's chosen theme without prop drilling.
 *
 * Also writes the current palette to `:root` CSS variables so pure-CSS or
 * legacy Tailwind class-based components can pick up the theme via
 * `var(--solano-hero-from)` / `var(--solano-accent)` etc.
 */

const LoadoutContext = createContext(null);

// Palette per theme item. Keep these values in sync with the store item keys
// registered in /app/backend/gamification.py -> STORE_ITEMS (category="theme").
export const THEME_PALETTES = {
  // Fallback / default (matches the previous hard-coded petrol gradient)
  default: {
    from: "#518688",
    via: "#5A8B8F",
    to: "#2C5254",
    accent: "#FC9700",
    accentSoft: "#FFD37E",
  },
  theme_forest: {
    from: "#2D5A3D",
    via: "#3E7C52",
    to: "#1F3D2A",
    accent: "#A8C34A",
    accentSoft: "#D4E17F",
  },
  theme_ocean: {
    from: "#1E5F8C",
    via: "#3A8DBF",
    to: "#0F3A5C",
    accent: "#5EDCF0",
    accentSoft: "#B3F0FA",
  },
  theme_sunset: {
    from: "#F4A261",
    via: "#E76F51",
    to: "#7A2E1F",
    accent: "#FFD166",
    accentSoft: "#FFE7A0",
  },
  // Sport-club inspired palettes. Kept intentionally generic (colour-name
  // based) — no team logos or trademarks. Each pairs a dominant hue with a
  // contrasting accent so charts, progress bars and CTAs stay readable.
  theme_bianconero: {
    // Black & white with a warm gold accent
    from: "#1A1A1A",
    via: "#3D3D3D",
    to: "#0A0A0A",
    accent: "#D4AF37",
    accentSoft: "#F4E5A8",
  },
  theme_nerazzurro: {
    // Deep blue-black with electric-cyan accent
    from: "#0B1F4A",
    via: "#153B7A",
    to: "#050D24",
    accent: "#4FB3FF",
    accentSoft: "#B3E0FF",
  },
  theme_rossonero: {
    // Crimson-black gradient with bright red accent
    from: "#4A0A0A",
    via: "#1A1A1A",
    to: "#2A0505",
    accent: "#E63946",
    accentSoft: "#FFB3B8",
  },
  theme_azzurro: {
    // Italian azure with tricolour green accent (subtle nod, not the flag)
    from: "#0072BB",
    via: "#3B99DE",
    to: "#004A80",
    accent: "#3AA35F",
    accentSoft: "#B8E4C6",
  },
  theme_giallorosso: {
    // Golden-yellow to deep amaranth-red
    from: "#F2C438",
    via: "#B8352F",
    to: "#5A0F0F",
    accent: "#FFD66B",
    accentSoft: "#FFE9A8",
  },
  theme_tricolore: {
    // Italian tricolour — green / white flash / red as a three-stop gradient
    from: "#008C45",
    via: "#F4F5F0",
    to: "#CD212A",
    accent: "#008C45",
    accentSoft: "#B7E4C4",
  },
  theme_neon: {
    // Cyberpunk violet / hot-pink / electric-blue
    from: "#3A0CA3",
    via: "#F72585",
    to: "#4361EE",
    accent: "#F72585",
    accentSoft: "#FFB8D9",
  },
  theme_lavender: {
    // Calm wellness lavender-to-plum
    from: "#B39DDB",
    via: "#7E57C2",
    to: "#4527A0",
    accent: "#CE93D8",
    accentSoft: "#E1BEE7",
  },
  theme_rosegold: {
    // Elegant rose-gold gradient with warm champagne accent
    from: "#E8B4B8",
    via: "#B76E79",
    to: "#7A3D48",
    accent: "#F4D06F",
    accentSoft: "#FFF0C1",
  },
  theme_matcha: {
    // Soft matcha green with cream accent
    from: "#7CB342",
    via: "#558B2F",
    to: "#33691E",
    accent: "#DCE775",
    accentSoft: "#F0F4C3",
  },
  theme_midnight: {
    // Deep space navy with starlight accent
    from: "#1A237E",
    via: "#283593",
    to: "#0D1338",
    accent: "#7986CB",
    accentSoft: "#C5CAE9",
  },
  // Seasonal limited-time palettes
  theme_summer: {
    // Summer turquoise — lagoon water with sun-yellow accent
    from: "#06AEBF",
    via: "#2EC4CE",
    to: "#0B5E68",
    accent: "#FFD166",
    accentSoft: "#FFE7A0",
  },
  theme_halloween: {
    // Pumpkin orange glowing out of a purple-black night
    from: "#3A1B4D",
    via: "#E2711D",
    to: "#1A0E24",
    accent: "#FF8C1A",
    accentSoft: "#FFC86B",
  },
  theme_christmas: {
    // Christmas red with fir green and gold accent
    from: "#7C1D2B",
    via: "#A62639",
    to: "#2E4A32",
    accent: "#F2C14E",
    accentSoft: "#F9E2A9",
  },
};

export const paletteFor = (key) => THEME_PALETTES[key] || THEME_PALETTES.default;

export function LoadoutProvider({ children }) {
  const [loadout, setLoadout] = useState({
    active_theme: null,
    equipped_badges: [],
    active_animations: [],
  });
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/client/gamification/loadout");
      setLoadout({
        active_theme: data?.active_theme || null,
        equipped_badges: data?.equipped_badges || [],
        active_animations: data?.active_animations || [],
      });
    } catch {
      // Anonymous / non-client visitors — keep defaults.
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const save = useCallback(async (patch) => {
    // Optimistic update, then reconcile with server response.
    setLoadout((prev) => ({ ...prev, ...patch }));
    try {
      const { data } = await api.post("/client/gamification/loadout", patch);
      setLoadout({
        active_theme: data?.active_theme || null,
        equipped_badges: data?.equipped_badges || [],
        active_animations: data?.active_animations || [],
      });
      return { ok: true };
    } catch (err) {
      // Revert on error
      refresh();
      return { ok: false, error: err?.response?.data?.detail || "error" };
    }
  }, [refresh]);

  const palette = useMemo(
    () => paletteFor(loadout.active_theme),
    [loadout.active_theme],
  );

  // Publish palette to :root CSS variables so any component (even without
  // reading the context) can pick up the theme via var(--solano-*).
  useEffect(() => {
    if (typeof document === "undefined") return;
    const r = document.documentElement.style;
    r.setProperty("--solano-hero-from", palette.from);
    r.setProperty("--solano-hero-via", palette.via);
    r.setProperty("--solano-hero-to", palette.to);
    r.setProperty("--solano-accent", palette.accent);
    r.setProperty("--solano-accent-soft", palette.accentSoft);
  }, [palette]);

  const themeGradient = useMemo(
    () => ({
      // Ready-to-use inline style for hero surfaces
      background: `linear-gradient(135deg, ${palette.from} 0%, ${palette.via} 50%, ${palette.to} 100%)`,
    }),
    [palette],
  );

  const value = useMemo(() => ({
    loadout,
    loaded,
    palette,
    themeGradient,
    hasAnimation: (key) => loadout.active_animations.includes(key),
    hasBadge: (key) => loadout.equipped_badges.includes(key),
    save,
    refresh,
  }), [loadout, loaded, palette, themeGradient, save, refresh]);

  return (
    <LoadoutContext.Provider value={value}>
      {children}
    </LoadoutContext.Provider>
  );
}

export const useLoadout = () => {
  const ctx = useContext(LoadoutContext);
  if (!ctx) {
    // Safe default so components used outside the provider (rare) still work.
    return {
      loadout: { active_theme: null, equipped_badges: [], active_animations: [] },
      loaded: false,
      palette: THEME_PALETTES.default,
      themeGradient: { background: `linear-gradient(135deg, ${THEME_PALETTES.default.from} 0%, ${THEME_PALETTES.default.via} 50%, ${THEME_PALETTES.default.to} 100%)` },
      hasAnimation: () => false,
      hasBadge: () => false,
      save: async () => ({ ok: false }),
      refresh: async () => {},
    };
  }
  return ctx;
};
